import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import { selectModeResolution } from 'redux/mode/reducers';
import { setActiveDsMode } from 'redux/mode/actions';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import SwatchPicker from 'components/Widget/SwatchPicker';

const BG_OPTIONS = [
	{ value: '#000000', label: 'Pure Black' },
	{ value: '#0f0f0f', label: 'Almost Black' },
	{ value: DS_EDITOR_PREVIEW_BG_DEFAULT, label: 'Off-White' },
	{ value: '#ffffff', label: 'Pure White' },
];

const PreviewBgPicker = () => {
	const dispatch = useDispatch();

	const { modes, activeModeId, defaultModeId, previewBg } = useSelector(
		(r: RootReducer) => selectModeResolution(r.mode),
		shallowEqual
	);
	const { activeDesignSystem, designSystemContent } = useSelector(
		(r: RootReducer) => ({
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			designSystemContent: r.mode.designSystemContent,
		}),
		shallowEqual
	);

	const hasMultipleModes = modes && modes.length >= 2;

	const onPreviewBgChange = async (newBg: string) => {
		if (!activeDesignSystem?.id) return;

		const displayFile = designSystemContent?.files?.find((f) => f.path === 'config/display.json');
		let existing: any = {};
		if (displayFile?.content) {
			try {
				existing = JSON.parse(displayFile.content);
			} catch (parseErr) {
				console.warn(
					'Failed to parse existing display.json, starting with empty object:',
					parseErr
				);
			}
		}

		let merged: any;
		if (activeModeId && Array.isArray(existing.modes) && existing.modes.length > 1) {
			merged = {
				...existing,
				modes: existing.modes.map((t: any) => (t.id === activeModeId ? { ...t, bg: newBg } : t)),
			};
		} else {
			merged = {
				...existing,
				bg: newBg,
			};
		}

		try {
			await forge.updateDsFiles({
				designSystemId: activeDesignSystem.id,
				files: [{ path: 'config/display.json', content: JSON.stringify(merged) }],
			});
		} catch (err) {
			console.error('Failed to update preview background:', err);
			toast.error('Failed to update preview background');
		}
	};

	const onSelect = (id: string) => {
		if (hasMultipleModes && modes) {
			dispatch(setActiveDsMode(id === defaultModeId ? null : id));
		} else {
			onPreviewBgChange(id);
		}
	};

	const options =
		hasMultipleModes && modes
			? modes.map((t) => ({
					id: t.id,
					color: t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT,
					label: t.label,
			  }))
			: BG_OPTIONS.map((opt) => ({ id: opt.value, color: opt.value, label: opt.label }));

	const activeId = hasMultipleModes && modes ? activeModeId : previewBg;

	return (
		<SwatchPicker
			options={options}
			activeId={activeId}
			defaultId={hasMultipleModes ? defaultModeId : undefined}
			triggerColor={previewBg}
			onSelect={onSelect}
			tooltip={hasMultipleModes ? 'Mode' : 'Preview background'}
		/>
	);
};

export default PreviewBgPicker;
